import { getCurrentDate } from "@/utils/common";
import type { FormInstance } from "antd/es/form";
import type { PersonnelInfo } from "../SupplierRiskLevel/components/RegisterRiskModal/types";

// ==================== 类型定义 ====================

/**
 * sessionStorage 中存储的用户数据结构
 */
interface UserData {
  purchaserUserId?: string;
  nickname?: string;
  username?: string;
  deptName?: string;
  deptNo?: string;
  postNo?: string;
  postName?: string;
  email?: string;
  userId?: string;
}

/**
 * 字段映射配置类型
 */
export type FieldMappingType = "personnel";

// ==================== 常量配置 ====================

/**
 * 字段映射配置
 * 将 sessionStorage 中的用户数据字段映射到表单字段
 */
const FIELD_MAPPING: Record<
  FieldMappingType,
  Record<string, keyof UserData | "static">
> = {
  personnel: {
    purchaseUserNickname: "nickname",
    purchaseUserUsername: "username",
    createTime: "static",
    purchaseUserDeptName: "deptName",
    purchaseUserDeptNo: "deptNo",
    purchaseUserPostName: "postName",
    purchaseUserPostNo: "postNo",
    purchaseUserEmail: "email",
    purchaserUserId: "userId",
  },
};

// ==================== 工具函数 ====================

/**
 * 从 sessionStorage 获取用户数据
 * @returns 解析后的用户数据，如果获取失败返回 null
 */
export const getUserDataFromSession = (): UserData | null => {
  const userDataStr = sessionStorage.getItem("user");

  if (!userDataStr) {
    return null;
  }

  try {
    return JSON.parse(userDataStr);
  } catch (error) {
    console.error("解析用户数据失败:", error);
    return null;
  }
};

/**
 * 从用户数据转换为人员信息
 * @param userData 从 sessionStorage 获取的用户数据
 * @returns 人员信息对象
 */
export const transformUserDataToPersonnelInfo = (
  userData: UserData,
): PersonnelInfo => {
  return {
    purchaseUserNickname: userData.nickname || "",
    purchaseUserUsername: userData.username || "",
    createTime: getCurrentDate(),
    purchaseUserDeptName: userData.deptName || "",
    purchaseUserDeptNo: userData.deptNo || "",
    purchaseUserPostName: userData.postName || "",
    purchaseUserPostNo: userData.postNo || "",
    purchaseUserEmail: userData.email || "",
    purchaserUserId: userData.userId || "",
  };
};

/**
 * 通用的字段映射转换函数
 * @param userData 用户数据
 * @param mappingType 字段映射类型
 * @returns 转换后的表单字段对象
 */
export const transformUserDataByMapping = (
  userData: UserData,
  mappingType: FieldMappingType,
): Record<string, string> => {
  const mapping = FIELD_MAPPING[mappingType];
  const result: Record<string, string> = {};

  Object.entries(mapping).forEach(([fieldName, sourceKey]) => {
    if (sourceKey === "static") {
      result[fieldName] = getCurrentDate();
    } else {
      result[fieldName] = userData[sourceKey] || "";
    }
  });

  return result;
};

/**
 * 获取人员信息（用于预填充表单）
 * 从 sessionStorage 获取用户数据并转换为人员信息对象
 * @returns 人员信息对象，如果获取失败返回 null
 */
export const getPersonnelInfo = (): PersonnelInfo | null => {
  const userData = getUserDataFromSession();

  if (!userData) {
    return null;
  }

  try {
    return transformUserDataToPersonnelInfo(userData);
  } catch (error) {
    console.error("获取人员信息失败:", error);
    return null;
  }
};

/**
 * 从 sessionStorage 获取用户数据并填充人员信息到表单
 * @param formRef 表单实例引用
 * @returns 是否成功获取和填充用户信息
 */
export const fillPersonnelInfo = (
  formRef: React.RefObject<FormInstance>,
): boolean => {
  const userData = getUserDataFromSession();

  if (!userData) {
    return false;
  }

  try {
    const userInfo = transformUserDataByMapping(userData, "personnel");
    formRef.current?.setFieldsValue(userInfo);
    return true;
  } catch (error) {
    console.error("填充人员信息失败:", error);
    return false;
  }
};

/**
 * 从 sessionStorage 获取用户数据并填充表单信息
 * 支持多种字段映射模式
 * @param formRef 表单实例引用
 * @param mappingType 字段映射类型，默认为 'personnel'
 * @returns 是否成功获取和填充用户信息
 */
export const fillFormWithUserInfo = (
  formRef: React.RefObject<FormInstance>,
  mappingType: FieldMappingType = "personnel",
): boolean => {
  const userData = getUserDataFromSession();

  if (!userData) {
    return false;
  }

  try {
    const userInfo = transformUserDataByMapping(userData, mappingType);
    formRef.current?.setFieldsValue(userInfo);
    return true;
  } catch (error) {
    console.error("填充用户信息失败:", error);
    return false;
  }
};
